









































































































import { defineComponent, ref, watch, computed } from '@vue/composition-api';
import SlidingPanel from '@front-app/components/modals/SlidingPanel.vue';
import CtaButton from '@front-app/components/buttons/CtaButton.vue';
import LoaderState from '@front-app/components/LoaderState.vue';
import ErrorCard from '@front-app/components/cards/state/ErrorCard.vue';
import { GroupEventsApi } from '@front-app/services/GroupEventsLinkingApi/GroupEventsApi';
import { EventItem } from '@front-app/services/GroupEventsLinkingApi/GroupEventsModel';
import { GroupsStore } from '@front-app/store/singletons/Groups';
import { ButtonPalettes } from '@front-app/components/buttons/palettes';
import { Size } from '@front-app/components/constants';
import ConfirmationModal from '@front-app/components/modals/ConfirmationModal.vue';

export default defineComponent({
  components: {
    SlidingPanel,
    CtaButton,
    LoaderState,
    ErrorCard,
    ConfirmationModal,
  },
  props: {
    isOpen: Boolean,
    groupIds: {
      type: Array as () => string[],
      required: true,
      default: () => [],
    },
    groupNames: {
      type: Array as () => string[],
      required: false,
      default: () => [],
    },
  },
  setup(props) {
    const ariaLabelId = 'link-events-modal-title';

    const isLoading = ref(false);
    const isError = ref(false);
    const isSubmitting = ref(false);
    const events = ref([] as EventItem[]);
    const selectedEventIdsLocal = ref<string[]>([]);
    const includePast = ref(false);
    const monthsBack = ref(1);
    const isConfirmOpen = ref(false);
    const successText = ref('');
    const errorText = ref('');

    const selectedGroupNames = computed(() => {
      const store = GroupsStore.getInstance();
      return (props.groupIds || []).map((id: string) => store.groupById(id)?.name || id);
    });
    const displayedGroupNames = computed(() => (props.groupNames && props.groupNames.length > 0
      ? props.groupNames
      : selectedGroupNames.value));

    const groupsList = computed(() => (props.groupIds || []).map((id: string, idx: number) => ({
      id,
      name: (props.groupNames && props.groupNames[idx]) || GroupsStore.getInstance().groupById(id)?.name || id,
    })));

    const selectedGroupIdsLocal = ref<string[]>([]);

    const fetchEvents = async () => {
      try {
        isLoading.value = true;
        isError.value = false;
        const res = await GroupEventsApi.searchEvents({ includePast: includePast.value, monthsBack: includePast.value ? monthsBack.value : undefined, page: 0, size: 10 });
        let items = res.items || [];
        const now = new Date();
        if (includePast.value) {
          const threshold = new Date(now);
          threshold.setMonth(threshold.getMonth() - (monthsBack.value || 1));
          items = items.filter((ev: EventItem) => {
            const d = new Date(ev.startDate);
            return d >= threshold; // includes future automatically
          });
        } else {
          items = items.filter((ev: EventItem) => new Date(ev.startDate) >= now);
        }
        events.value = items;
        // Prune selected events that are no longer visible (e.g., after toggling Include Past)
        const visible = new Set((events.value || []).map((e: EventItem) => e.eventId));
        selectedEventIdsLocal.value = selectedEventIdsLocal.value.filter((id: string) => visible.has(id));
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to load events', e);
        isError.value = true;
      } finally {
        isLoading.value = false;
      }
    };

    const refetchEvents = () => {
      fetchEvents();
    };

    watch(() => props.isOpen, (open) => {
      if (open) {
        selectedEventIdsLocal.value = [];
        successText.value = '';
        errorText.value = '';
        selectedGroupIdsLocal.value = [...(props.groupIds || [])];
        includePast.value = false;
        monthsBack.value = 1;
        fetchEvents();
      }
    });

    const formatDate = (iso: string) => new Date(iso).toLocaleString();
    const isPastEvent = (ev: EventItem) => {
      try {
        return new Date(ev.startDate).getTime() < Date.now();
      } catch {
        return false;
      }
    };
    const submitLinks = async () => {
      if (selectedEventIdsLocal.value.length === 0) return;
      try {
        isSubmitting.value = true;
        successText.value = '';
        errorText.value = '';
        const res = await GroupEventsApi.createLinks({
          eventIds: selectedEventIdsLocal.value,
          groupIds: selectedGroupIdsLocal.value,
        });
        successText.value = res.message || `Successfully created ${res.linksCreated} event-group links`;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to create links', e);
        const apiMessage = (e && (e as any).response && (e as any).response.data && (e as any).response.data.message) || 'Failed to create links';
        errorText.value = apiMessage;
      } finally {
        isSubmitting.value = false;
      }
    };

    const linkCount = computed(() => (selectedEventIdsLocal.value.length * selectedGroupIdsLocal.value.length));
    const canSubmit = computed(() => (selectedEventIdsLocal.value.length > 0 && selectedGroupIdsLocal.value.length > 0));
    const openConfirm = () => {
      if (selectedEventIdsLocal.value.length === 0 || selectedGroupIdsLocal.value.length === 0) return;
      isConfirmOpen.value = true;
    };
    const handleConfirm = async () => {
      isConfirmOpen.value = false;
      await submitLinks();
    };

    return {
      ariaLabelId,
      isLoading,
      isError,
      isSubmitting,
      events,
      selectedEventIdsLocal,
      successText,
      errorText,
      selectedGroupNames,
      displayedGroupNames,
      groupsList,
      selectedGroupIdsLocal,
      includePast,
      monthsBack,
      refetchEvents,
      formatDate,
      isPastEvent,
      submitLinks,
      openConfirm,
      isConfirmOpen,
      handleConfirm,
      linkCount,
      canSubmit,
      ButtonPalettes,
      Size,
    };
  },
});
